import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { LogOut, ClipboardList, Users, Store, FileSpreadsheet, UserCog, Pizza, ReceiptText, Wheat, TrendingUp } from "lucide-react";
import { Button } from "@/components/ui/button";

const linkClass = ({ isActive }) =>
  `flex items-center gap-3 px-3 py-2 text-sm transition-colors border-l-2 ${
    isActive
      ? "bg-zinc-950 text-white border-zinc-950"
      : "text-zinc-700 border-transparent hover:bg-zinc-100"
  }`;

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  if (!user) return null;

  return (
    <div className="min-h-screen flex bg-[#FAFAFA]">
      <aside className="w-64 shrink-0 border-r border-zinc-200 bg-white flex flex-col" data-testid="sidebar">
        <div className="px-5 py-5 border-b border-zinc-200 flex items-center gap-2">
          <div className="w-8 h-8 bg-[#DF1A22] flex items-center justify-center">
            <Pizza className="w-4 h-4 text-white" strokeWidth={2.5} />
          </div>
          <div>
            <div className="font-display font-bold text-base leading-none">LABOUR</div>
            <div className="text-[10px] tracking-[0.18em] text-zinc-500 mt-1">PAPA JOHN'S TRACKER</div>
          </div>
        </div>

        <nav className="flex-1 py-3">
          {user.role === "admin" ? (
            <>
              <div className="label-uc px-5 py-2">Admin</div>
              <NavLink to="/admin/entries" className={linkClass} data-testid="nav-admin-entries">
                <FileSpreadsheet className="w-4 h-4" /> Daily Entries
              </NavLink>
              <NavLink to="/admin/sales" className={linkClass} data-testid="nav-admin-sales">
                <ReceiptText className="w-4 h-4" /> Sales
              </NavLink>
              <NavLink to="/admin/profit" className={linkClass} data-testid="nav-admin-profit">
                <TrendingUp className="w-4 h-4" /> Profit & Loss
              </NavLink>
              <NavLink to="/admin/stores" className={linkClass} data-testid="nav-admin-stores">
                <Store className="w-4 h-4" /> Stores
              </NavLink>
              <NavLink to="/admin/managers" className={linkClass} data-testid="nav-admin-managers">
                <UserCog className="w-4 h-4" /> Managers
              </NavLink>
            </>
          ) : (
            <>
              <div className="label-uc px-5 py-2">Manager</div>
              <NavLink to="/manager" className={linkClass} data-testid="nav-manager-dashboard">
                <ClipboardList className="w-4 h-4" /> Daily Labour
              </NavLink>
              <NavLink to="/manager/sales" className={linkClass} data-testid="nav-manager-sales">
                <ReceiptText className="w-4 h-4" /> Daily Sales
              </NavLink>
              <NavLink to="/manager/food-cost" className={linkClass} data-testid="nav-manager-food-cost">
                <Wheat className="w-4 h-4" /> Food Cost
              </NavLink>
              <NavLink to="/manager/roster" className={linkClass} data-testid="nav-manager-roster">
                <Users className="w-4 h-4" /> Employees
              </NavLink>
            </>
          )}
        </nav>

        <div className="p-4 border-t border-zinc-200">
          <div className="text-xs text-zinc-500 mb-1">Signed in as</div>
          <div className="text-sm font-medium truncate" data-testid="current-user-name">{user.username || user.name}</div>
          <div className="label-uc mt-1">{user.role}</div>
          <Button
            variant="outline"
            size="sm"
            onClick={handleLogout}
            className="mt-3 w-full rounded-none border-zinc-300 hover:bg-zinc-100"
            data-testid="logout-button"
          >
            <LogOut className="w-3 h-3 mr-2" /> Sign out
          </Button>
        </div>
      </aside>

      <main className="flex-1 min-w-0">
        <Outlet />
      </main>
    </div>
  );
}
